import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from '@radix-ui/react-slot'
import type { ComponentProps } from 'react'
import { cn } from '@/lib/cn'

export const buttonVariants = cva(
  // Design system button: uppercase, semibold, slightly tracked, 4px corners, 120ms colour changes.
  'inline-flex shrink-0 items-center justify-center gap-2 max-w-full rounded-control border border-transparent text-center leading-tight font-semibold tracking-[0.04em] uppercase transition-colors duration-150 ease-(--ease-standard) select-none disabled:pointer-events-none disabled:opacity-45 [&_svg]:shrink-0 [&_svg]:stroke-[1.75]',
  {
    variants: {
      variant: {
        // Green with black text: the only accessible way to put text on Mez green.
        primary: 'bg-brand text-ink shadow-xs hover:bg-brand-glow active:bg-[#549413]',
        secondary:
          'bg-ink text-white hover:bg-[#2a2d26] active:bg-[#40443c] dark:bg-white dark:text-ink dark:hover:bg-[#dde0d9]',
        outline: 'border-border-strong bg-surface text-fg hover:border-fg hover:bg-surface-2',
        ghost: 'text-brand-text hover:bg-brand-tint',
        danger: 'bg-danger text-white hover:opacity-90 dark:text-black',
        link: 'normal-case tracking-normal text-brand-text underline underline-offset-4 hover:no-underline',
      },
      size: {
        sm: 'min-h-9 px-3 text-xs [&_svg]:size-4',
        md: 'min-h-tap px-5 text-sm [&_svg]:size-5',
        lg: 'min-h-tap-lg px-7 text-base [&_svg]:size-5',
        // The huge "New Relux" button on the rep home screen.
        hero: 'min-h-24 w-full rounded-panel px-6 text-xl font-bold [&_svg]:size-8',
        icon: 'size-tap [&_svg]:size-6',
      },
      block: { true: 'w-full' },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
)

export type ButtonProps = ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & { asChild?: boolean }

export function Button({ className, variant, size, block, asChild, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : 'button'
  return <Comp className={cn(buttonVariants({ variant, size, block }), className)} {...props} />
}
